import React, { useState, useEffect, useCallback } from 'react';
import TrainingGuide from '../common/TrainingGuide';
import inventorySteps from '../../data/training/inventory';
import KPICards from '../inventory/KPICards';
import SalesChart from '../inventory/SalesChart';
import TopProducts from '../inventory/TopProducts';
import DeadStock from '../inventory/DeadStock';
import LowStockAlert from '../inventory/LowStockAlert';
import VelocityGauge from '../inventory/VelocityGauge';
import AICommentary from '../inventory/AICommentary';
import { BRAND } from '../../branding';
import '../../styles/inventory.css';

// All API calls use relative paths â€” nginx routes /api/* â†’ FastAPI
const apiFetch = async (path, options = {}) => {
  try {
    const response = await fetch(path, {
      ...options,
      credentials: 'include', // Important for authentication cookies
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`API call failed for ${path}:`, error);
    throw error;
  }
};

const TABS = [
  { id: 'overview', label: 'ðŸ“Š Overview' },
  { id: 'sales',    label: 'ðŸ’° Sales' },
  { id: 'top',      label: 'ðŸ† Top 10' },
  { id: 'dead',     label: 'ðŸ’€ Dead Stock' },
  { id: 'low',      label: 'ðŸ”´ Low Stock' },
  { id: 'velocity', label: 'âš¡ Velocity' },
];

export default function InventoryDashboard() {
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [data, setData] = useState(null);
  const [commentary, setCommentary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/inventory/dashboard/full');
      setData(res.data);
      setCommentary(res.ai_commentary || []);
      setLastRefresh(new Date().toLocaleTimeString());
    } catch (err) {
      setError(err.message || 'Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      await apiFetch('/api/inventory/refresh', { method: 'POST' });
      await loadDashboard();
    } catch (err) {
      setError(`Refresh failed: ${err.message}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    loadDashboard();
  };

  if (loading && !data) {
    return (
      <div className="inv-loading">
        <div className="inv-loading-orb"></div>
        <h2 className="inv-loading-text">Initializing Intelligence...</h2>
        <p className="inv-loading-sub">Connecting to Shopify â€¢ Analyzing Inventory</p>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="inv-error">
        <div style={{ fontSize: '3rem' }}>âš ï¸</div>
        <h2>Connection Error</h2>
        <p>{error}</p>
        <button className="inv-btn-primary" onClick={handleRetry}>
          Retry Connection
        </button>
      </div>
    );
  }

  return (
    <div className="inv-dashboard">
      {/* HEADER */}
      <header className="inv-header">
        <div className="inv-header-left">
          <h1 className="inv-logo">
            <span className="inv-logo-icon">â—†</span>
            INVENTORY<span className="inv-logo-accent">INTEL</span>
          </h1>
          <span className="inv-subtitle">AFAQ ALNASEEM â€¢ Shopify Analytics</span>
        </div>
        <div className="inv-header-right">
          {lastRefresh && (
            <span className="inv-last-refresh">
              Last: {lastRefresh}
              {isRefreshing && ' (Refreshing...)'}
            </span>
          )}
          <button 
            className="inv-btn-refresh" 
            onClick={handleRefresh} 
            disabled={isRefreshing}
          >
            {isRefreshing ? 'âŸ³' : 'â†»'} Refresh
          </button>
          <button 
            onClick={() => setTrainingOpen(true)} 
            title="Open training guide"
            className="inv-btn-training"
          >
            ?
          </button>
        </div>
      </header>
      
      <TrainingGuide 
        moduleKey="inventory" 
        steps={inventorySteps} 
        isOpen={trainingOpen} 
        onClose={() => setTrainingOpen(false)} 
      />

      {/* KPI STRIP */}
      {data?.kpis && (
        <KPICards kpis={data.kpis} channelSummary={data.channel_summary} />
      )}

      {/* TABS */}
      <nav className="inv-tab-nav">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`inv-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* CONTENT */}
      <main className="inv-content">
        {error && data && (
          <div className="inv-error-banner">
            <p>âš ï¸ {error}</p>
            <button onClick={handleRetry}>Retry</button>
          </div>
        )}

        {activeTab === 'overview' && (
          <div className="inv-grid-overview">
            <div className="inv-card inv-card-wide">
              <SalesChart data={data?.sales_chart || []} />
            </div>
            <div className="inv-card">
              <TopProducts data={data?.top_products || []} compact />
            </div>
            <div className="inv-card">
              <LowStockAlert data={data?.low_stock || []} compact />
            </div>
            <div className="inv-card inv-card-wide">
              <AICommentary commentary={commentary} />
            </div>
          </div>
        )}

        {activeTab === 'sales' && (
          <div className="inv-grid-single">
            <div className="inv-card">
              <SalesChart data={data?.sales_chart || []} full />
            </div>
            <AICommentary commentary={commentary} filter="sales" />
          </div>
        )}

        {activeTab === 'top' && (
          <div className="inv-grid-single">
            <div className="inv-card">
              <TopProducts data={data?.top_products || []} />
            </div>
            <AICommentary commentary={commentary} filter="top_products" />
          </div>
        )}

        {activeTab === 'dead' && (
          <div className="inv-grid-single">
            <div className="inv-card">
              <DeadStock data={data?.dead_stock || []} />
            </div>
            <AICommentary commentary={commentary} filter="dead_stock" />
          </div>
        )}

        {activeTab === 'low' && (
          <div className="inv-grid-single">
            <div className="inv-card">
              <LowStockAlert data={data?.low_stock || []} />
            </div>
            <AICommentary commentary={commentary} filter="low_stock" />
          </div>
        )}

        {activeTab === 'velocity' && (
          <div className="inv-grid-single">
            <div className="inv-card">
              <VelocityGauge data={data?.velocity || []} />
            </div>
            <AICommentary commentary={commentary} filter="velocity" />
          </div>
        )}
      </main>

      <footer className="inv-footer">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <img 
            src="https://cdn.shopify.com/s/files/1/0911/0215/0954/files/Veridian_logo_2.png?v=1775880420" 
            alt="Veridian" 
            style={{ 
              width: 16, 
              height: 16, 
              objectFit: "contain" 
            }} 
          />
          Powered by Veridian
        </span>
        <span>Â© {new Date().getFullYear()} AFAQ ALNASEEM TRADING L.L.C</span>
      </footer>
    </div>
  );
}
